import {
  Button,
  Table,
  TableCell,
  TableColumnHeader,
  TableRow,
  TableRowHeader
} from '@/common'
import useI18n from '@/shared/hooks/usei18n'
import { SupplierFormData } from '../SupplierForm.schema'

type Props = {
  data: SupplierFormData
  onNextClick: () => void
  onBackClick: () => void
}

export default function SupplierReviewStep({ onNextClick, onBackClick, data }: Props) {
  const { t } = useI18n()

  const handleNext = () => {
    return onNextClick()
  }

  return (
    <div className="space-y-6 pb-2">
      <div>
        <h2 className="text-xl font-semibold mb-2">
          {t('suppliers:createSupplierDialog.steps.descriptions.review.title')}
        </h2>
        <p className="text-sm text-text-secondary mb-6">
          {t('suppliers:createSupplierDialog.steps.descriptions.review.description')}
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-4">
            <Table superDense className="table-fixed">
              <thead>
                <TableRow>
                  <TableColumnHeader colSpan={2}>
                    {t('suppliers:supplierInputFields.sections.suppliersDetails')}
                  </TableColumnHeader>
                </TableRow>
              </thead>
              <tbody className="text-sm">
                <TableRow>
                  <TableRowHeader className="text-left">
                    {t('suppliers:supplierInputFields.supplierName')}
                  </TableRowHeader>
                  <TableCell>{data?.supplierName}</TableCell>
                </TableRow>
                <TableRow>
                  <TableRowHeader className="text-left text-nowrap truncate">
                    {t('suppliers:supplierInputFields.businessRegistrationNumber.label')}
                  </TableRowHeader>
                  <TableCell>{data?.businessRegistrationNumber || t('common:unknown')}</TableCell>
                </TableRow>
              </tbody>
            </Table>
            <Table superDense className="table-fixed">
              <thead>
                <TableRow>
                  <TableColumnHeader colSpan={2}>
                    {t('suppliers:supplierInputFields.sections.contactDetails')}
                  </TableColumnHeader>
                </TableRow>
              </thead>
              <tbody className="text-sm">
                <TableRow>
                  <TableRowHeader className="text-left">
                    {t('suppliers:supplierInputFields.contactName')}
                  </TableRowHeader>
                  <TableCell>{data?.contactName}</TableCell>
                </TableRow>
                <TableRow>
                  <TableRowHeader className="text-left">
                    {t('suppliers:supplierInputFields.contactPhoneNumber')}
                  </TableRowHeader>
                  <TableCell>{data?.contactPhoneNumber}</TableCell>
                </TableRow>
                <TableRow>
                  <TableRowHeader className="text-left">
                    {t('suppliers:supplierInputFields.contactEmail')}
                  </TableRowHeader>
                  <TableCell className="text-nowrap truncate">{data?.contactEmail}</TableCell>
                </TableRow>
              </tbody>
            </Table>
          </div>

          <Table superDense className="table-fixed">
            <thead>
              <TableRow>
                <TableColumnHeader colSpan={2}>
                  {t('suppliers:supplierInputFields.sections.address')}
                </TableColumnHeader>
              </TableRow>
            </thead>
            <tbody className="text-sm">
              <TableRow>
                <TableRowHeader className="text-left">
                  {t('common:inputFields.street.label')}
                </TableRowHeader>
                <TableCell>{data?.street}</TableCell>
              </TableRow>
              <TableRow>
                <TableRowHeader className="text-left">
                  {t('common:inputFields.streetNumber.label')}
                </TableRowHeader>
                <TableCell>{data?.streetNumber}</TableCell>
              </TableRow>
              <TableRow>
                <TableRowHeader className="textz-left text-nowrap truncate">
                  {t('common:inputFields.street2.label')}
                </TableRowHeader>
                <TableCell>{data?.street2 || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableRowHeader className="text-left">
                  {t('common:inputFields.postCode.label')}
                </TableRowHeader>
                <TableCell>{data?.postCode}</TableCell>
              </TableRow>
              <TableRow>
                <TableRowHeader className="text-left">
                  {t('common:inputFields.city.label')}
                </TableRowHeader>
                <TableCell>{data?.city}</TableCell>
              </TableRow>
              <TableRow>
                <TableRowHeader className="text-left">
                  {t('common:inputFields.poBox.label')}
                </TableRowHeader>
                <TableCell>{data?.poBox || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableRowHeader className="text-left text-nowrap truncate">
                  {t('common:inputFields.subDivision.label')}
                </TableRowHeader>
                <TableCell>{data?.subDivision ? data?.subDivision : '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableRowHeader className="text-left">
                  {t('common:inputFields.country.label')}
                </TableRowHeader>
                <TableCell>{t(`common:countriesByIsoAlpha2:${data?.country}`)}</TableCell>
              </TableRow>
            </tbody>
          </Table>
        </div>
      </div>
      <div className="flex justify-between">
        <Button variant="neutral" onClick={onBackClick}>
          {t('common:actions.back')}
        </Button>
        <Button onClick={handleNext}>{t('suppliers:createSupplierDialog.actions.continue')}</Button>
      </div>
    </div>
  )
}
